import type { SDKEventType, SDKEventPayload, SDKEventHandler } from '../types';
import { logger } from './logger';

type AnyHandler = (payload: unknown) => void;

export class EventBus {
  private listeners: Map<SDKEventType, Set<AnyHandler>> = new Map();

  on<T extends keyof SDKEventPayload>(event: T, handler: SDKEventHandler<T>): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(handler as AnyHandler);
    
    logger.debug(`Event listener added: ${event}`);
    
    // Return unsubscribe function
    return () => this.off(event, handler);
  }

  off<T extends keyof SDKEventPayload>(event: T, handler: SDKEventHandler<T>): void {
    const handlers = this.listeners.get(event);
    if (handlers) {
      handlers.delete(handler as AnyHandler);
      logger.debug(`Event listener removed: ${event}`);
    }
  }

  emit<T extends keyof SDKEventPayload>(event: T, payload: SDKEventPayload[T]): void {
    logger.debug(`Event emitted: ${event}`, payload);
    
    const handlers = this.listeners.get(event);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          (handler as SDKEventHandler<T>)(payload);
        } catch (error) {
          logger.error(`Error in event handler for ${event}:`, error);
        }
      });
    }
  }

  once<T extends keyof SDKEventPayload>(event: T, handler: SDKEventHandler<T>): () => void {
    const wrappedHandler: SDKEventHandler<T> = (payload) => {
      this.off(event, wrappedHandler);
      handler(payload);
    };
    return this.on(event, wrappedHandler);
  }

  removeAllListeners(event?: SDKEventType): void {
    if (event) {
      this.listeners.delete(event);
    } else {
      this.listeners.clear();
    }
  }
}

// Singleton event bus instance
export const eventBus = new EventBus();

